import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit, Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BirthDateUtils} from '../../../../../shared/utils/birthdate.utils';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {PhoneInputComponent} from '../../../../common/phone/component/phone-input/phone-input.component';
import {PersonSummaryModel} from '../../entity/personSummary.model';
import {PersonSavePayload} from '../../../impl/service/payload/personSave.payload';
import {PhoneSavePayload} from '../../../../common/phone/payload/phoneSave.payload';
import {AccountSummary} from '../../../../../shared/model/account/accountSummary.model';
import {MomentAdapter} from "../../../../../shared/utils/moment.utils";

@Component({
    selector: 'app-person-update-form',
    templateUrl: './person-update-form.component.html'
})
export class PersonUpdateFormComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

    @Input() parentFormGroup: UntypedFormGroup;
    @Input() birthDateRequired = false;
    @Input() displayTitleValue = true;
    @Input() withFormGroups = true;
    @Input() person: PersonSummaryModel;
    @Input() initialPayload: PersonSavePayload;
    @Input() account: AccountSummary;
    @Input() displayDeadControl = true;

    @Output() nameChangedEvent: EventEmitter<string> = new EventEmitter();

    @ViewChild('phoneComponent') phoneComponent: PhoneInputComponent;
    @ViewChild('mobilePhoneComponent') mobilePhoneComponent: PhoneInputComponent;
    @ViewChild('officePhoneComponent') officePhoneComponent: PhoneInputComponent;

    phonePayload: PhoneSavePayload;
    mobilePhonePayload: PhoneSavePayload;
    officePhonePayload: PhoneSavePayload;

    loading = false;
    formGroup: UntypedFormGroup;
    birthDateFormCtrl = new UntypedFormControl('');
    F;
    /** Subject that emits when the component has been destroyed. */
    private _onDestroy = new Subject<void>();

    constructor(private formBuilder: UntypedFormBuilder) {
        this.formGroup = this.formBuilder.group({
            lastName: new UntypedFormControl('', Validators.required),
            firstName: new UntypedFormControl('', Validators.required),
            civility: new UntypedFormControl('MR', Validators.required),
            titleValue: new UntypedFormControl('PERSON_TITLE_MISTER_MADAM', Validators.required),
            birthDate: this.birthDateFormCtrl,
            email: new UntypedFormControl('', Validators.compose([Validators.maxLength(70), Validators.pattern('^[_A-Za-z0-9-\\+]+(\\.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)*(\\.[A-Za-z]{2,})$')])),
            phone: new UntypedFormGroup({}),
            officePhone: new UntypedFormGroup({}),
            mobilePhone: new UntypedFormGroup({}),
            dead: new UntypedFormControl(false),
            id: new UntypedFormControl('')
        });

        this.setTitleValueRequired();
    }

    ngOnInit() {
        this.enrichFormGroup();
    }

    ngAfterViewInit(): void {
        this.loadEntityFromProp();
    }


    ngOnDestroy(): void {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const parentFormGroupChange = changes['parentFormGroup'];
        if (parentFormGroupChange && parentFormGroupChange.currentValue && !parentFormGroupChange.isFirstChange()) {
            this.enrichFormGroup();
        }

        const birthDateRequiredChange = changes['birthDateRequired'];
        if (birthDateRequiredChange) {
            this.setBirthDateRequired();
            if (!this.birthDateFormCtrl.value) {
                this.birthDateFormCtrl.setValue(MomentAdapter.current().get());
            }
        }

        const displayTitleValueChange = changes['displayTitleValue'];
        if (displayTitleValueChange) {
            this.setTitleValueRequired();
        }

        const personChange = changes['person'];
        if (personChange && !personChange.firstChange) {
            this.loadEntityFromProp();
        }

        if (changes['initialPayload'] && this.initialPayload) {
            this.formGroup.patchValue({
                civility: this.initialPayload?.civility,
                lastName: this.initialPayload?.lastName,
                firstName: this.initialPayload?.firstName,
                birthDate: this.initialPayload?.birthDate,
                titleValue: this.initialPayload?.titleValue,
                email: this.initialPayload?.email,
                id: this.initialPayload?.id,
                dead: this.initialPayload?.dead,
            });
            this.officePhonePayload = this.initialPayload?.officePhone;
            this.phonePayload = this.initialPayload?.phone;
            this.mobilePhonePayload = this.initialPayload?.mobilePhone;
        }
    }

    private loadEntityFromProp() {
        if (this.person) {
            this.loading = true;
            this.entityLoaded(this.person);
            this.loading = false;
        }
    }

    public entityLoaded(entity: PersonSummaryModel) {
        // console.logByType['entityLoaded', entity, this.formGroup]);
        this.formGroup.patchValue({
            civility: entity?.civility?.value,
            lastName: entity?.lastName,
            firstName: entity?.firstName,
            birthDate: entity?.birthDate,
            titleValue: entity?.title?.value,
            email: entity?.email,
            id: entity?.id,
            dead: entity?.dead
        });

        this.officePhoneComponent.entityLoaded(entity?.officePhone);
        this.mobilePhoneComponent.entityLoaded(entity?.mobilePhone);
        this.phoneComponent.entityLoaded(entity?.phone);
    }

    private enrichFormGroup() {
        this.parentFormGroup.removeControl('person');
        this.parentFormGroup.addControl('person', this.formGroup);
        this.setBirthDateRequired();
    }

    private setBirthDateRequired() {
        this.birthDateFormCtrl.setValidators(this.birthDateRequired ? Validators.required : []);
    }

    private setTitleValueRequired() {
        this.birthDateFormCtrl.setValidators(this.displayTitleValue ? Validators.required : []);
    }

    nameUpdated() {
        const gender = this.formGroup.get('civility')?.value;
        const titleValue = this.formGroup.get('titleValue')?.value;
        let titleLabel = gender && titleValue ?  PersonSummaryModel.resolveTileLabel(titleValue, gender) : '';

        const firstName = this.formGroup.get('firstName')?.value ?? '';
        const lastName = this.formGroup.get('lastName')?.value ?? '';
        const fullName = `${titleLabel} ${firstName} ${lastName}`.trim();
        this.nameChangedEvent.emit(fullName);
    }


    get email() {
        return this.formGroup.get('email');
    }

    get phone(): AbstractControl {
        return this.formGroup.get('phone');
    }

    get mobilePhone(): AbstractControl {
        return this.formGroup.get('mobilePhone');
    }

    get officePhone(): AbstractControl {
        return this.formGroup.get('officePhone');
    }


}
